// frontend/src/pages/OLTOnus.jsx
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "../api/axios";
import { deleteONU, getOnusFilters } from "../api/olt.api";

const LOW_SIGNAL_MIN = -25;
const LOW_SIGNAL_MAX = -12;

// ==========================================================
// UTILIDADES
// ==========================================================
const fmtRelativeTime = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "hace instantes";
    if (diff < 3600) return `hace ${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `hace ${Math.floor(diff / 3600)}h`;
    if (diff < 604800) return `hace ${Math.floor(diff / 86400)}d`;
    return d.toLocaleDateString();
  } catch {
    return "—";
  }
};

const fmtRxPower = (rx) => {
  if (rx === null || rx === undefined) return "—";
  return `${Number(rx).toFixed(2)} dBm`;
};

const rxBadgeClass = (rx) => {
  if (rx === null || rx === undefined) return "text-gray-400";
  if (rx < LOW_SIGNAL_MIN || rx > LOW_SIGNAL_MAX) return "text-red-600 font-semibold";
  return "text-green-700";
};

// ==========================================================
// NORMALIZAR ÍNDICE DE ONU
// ==========================================================
// El backend usa formato "0-2-10-5" (guiones)
// El detalle (onu.fsp_onu) viene como "0/2/10:5" (slash + dos puntos)
// Esta función normaliza cualquiera al formato del backend.
const normalizeOnuIndex = (onu) => {
  // Preferir el "index" crudo si existe (ya viene en formato "0-2-10-5")
  if (onu.index) return onu.index;

  // Si no, construir desde fsp_onu
  if (onu.fsp_onu) {
    return onu.fsp_onu
      .replace(/\//g, "-")   // 0/2/10  → 0-2-10
      .replace(/:/g, "-");   // 0-2-10:5 → 0-2-10-5
  }

  // Fallback con pon + onu_id
  if (onu.pon && onu.onu_id !== undefined) {
    return `${onu.pon.replace(/\//g, "-")}-${onu.onu_id}`;
  }

  return "";
};

// ==========================================================
// COMPONENTE PRINCIPAL
// ==========================================================
export default function OLTOnus() {
  const { oltId } = useParams();
  const navigate = useNavigate();

  const [oltName, setOltName] = useState("");
  const [onus, setOnus] = useState([]);
  const [loading, setLoading] = useState(true);

  // ====== FILTROS ======
  const [statusFilter, setStatusFilter] = useState("all");
  const [powerFilter, setPowerFilter] = useState("all");
  const [modelFilter, setModelFilter] = useState("");
  const [vlanFilter, setVlanFilter] = useState("");
  const [frameFilter, setFrameFilter] = useState("");
  const [slotFilter, setSlotFilter] = useState("");
  const [portFilter, setPortFilter] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");

  // Ordenamiento
  const [sortField, setSortField] = useState("first_seen");
  const [sortOrder, setSortOrder] = useState("desc");

  // Opciones para los selectores
  const [filterOptions, setFilterOptions] = useState({
    frames: [], slots: [], ports: [], models: [], statuses: [], vlans: [],
  });

  // ====== PAGINACIÓN ======
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);

  // ==========================================================
  // CARGA DE DATOS
  // ==========================================================
  const loadData = useCallback(async () => {
    try {
      setLoading(true);

      const params = {
        page, limit,
        sort: sortField,
        order: sortOrder,
      };

      if (search.trim()) params.search = search.trim();
      if (statusFilter !== "all") params.status = statusFilter;
      if (powerFilter === "bad") params.power = "bad";
      if (modelFilter) params.model = modelFilter;
      if (vlanFilter !== "") params.vlan = vlanFilter;
      if (frameFilter !== "") params.frame = frameFilter;
      if (slotFilter !== "") params.slot = slotFilter;
      if (portFilter !== "") params.port = portFilter;

      const res = await axios.get(`/olts/${oltId}/onus`, { params });
      setOltName(res.data?.olt_name || `OLT #${oltId}`);
      setOnus(res.data?.onus || []);
      setTotal(res.data?.total || 0);
      setPages(res.data?.pages || 1);
    } catch (err) {
      console.error("Error cargando ONUs:", err);
      setOnus([]); setTotal(0); setPages(1);
    } finally {
      setLoading(false);
    }
  }, [oltId, page, limit, search, statusFilter, powerFilter,
      modelFilter, vlanFilter, frameFilter, slotFilter, portFilter,
      sortField, sortOrder]);

  const loadFilterOptions = useCallback(async () => {
    try {
      const data = await getOnusFilters(oltId);
      setFilterOptions(data);
    } catch (err) {
      console.error("Error cargando opciones de filtros:", err);
    }
  }, [oltId]);

  useEffect(() => {
    if (!oltId) return;
    loadData();
  }, [oltId, loadData]);

  useEffect(() => {
    if (!oltId) return;
    loadFilterOptions();
  }, [oltId, loadFilterOptions]);

  // ==========================================================
  // ELIMINAR ONU
  // ==========================================================
  const handleDeleteONU = async (onuIndex, onuName) => {
    const confirmDelete = window.confirm(
      `¿Estás seguro de que deseas eliminar la ONU "${onuName}"?\n\nEsta acción no se puede deshacer.`
    );
    if (!confirmDelete) return;
    try {
      setLoading(true);
      await deleteONU(oltId, onuIndex);
      alert(`✅ ONU "${onuName}" eliminada`);
      loadData();
    } catch (error) {
      alert(`❌ Error: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // ==========================================================
  // VER ONU (navegación segura)
  // ==========================================================
  const handleViewONU = (onu) => {
    const idx = normalizeOnuIndex(onu);
    if (!idx) {
      alert("No se pudo determinar el índice de la ONU");
      return;
    }
    navigate(`/olts/${oltId}/onus/${idx}`);
  };

  // ==========================================================
  // APLICAR FILTROS / ORDENAMIENTO
  // ==========================================================
  const applySearch = () => {
    setPage(1);
    setSearch(searchInput);
  };

  const clearAllFilters = () => {
    setStatusFilter("all");
    setPowerFilter("all");
    setModelFilter("");
    setVlanFilter("");
    setFrameFilter("");
    setSlotFilter("");
    setPortFilter("");
    setSearchInput("");
    setSearch("");
    setPage(1);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("desc");
    }
    setPage(1);
  };

  // ==========================================================
  // CHIPS DE FILTROS ACTIVOS
  // ==========================================================
  const activeFilters = useMemo(() => {
    const chips = [];
    if (search) chips.push({ key: "search", label: `Buscar: "${search}"`, clear: () => { setSearch(""); setSearchInput(""); } });
    if (statusFilter !== "all") chips.push({ key: "status", label: `Estado: ${statusFilter}`, clear: () => setStatusFilter("all") });
    if (powerFilter === "bad") chips.push({ key: "power", label: "Potencia fuera de rango", clear: () => setPowerFilter("all") });
    if (modelFilter) chips.push({ key: "model", label: `Modelo: ${modelFilter}`, clear: () => setModelFilter("") });
    if (vlanFilter !== "") chips.push({ key: "vlan", label: `VLAN: ${vlanFilter}`, clear: () => setVlanFilter("") });
    if (frameFilter !== "") chips.push({ key: "frame", label: `Frame: ${frameFilter}`, clear: () => setFrameFilter("") });
    if (slotFilter !== "") chips.push({ key: "slot", label: `Slot: ${slotFilter}`, clear: () => setSlotFilter("") });
    if (portFilter !== "") chips.push({ key: "port", label: `Puerto: ${portFilter}`, clear: () => setPortFilter("") });
    return chips;
  }, [search, statusFilter, powerFilter, modelFilter, vlanFilter, frameFilter, slotFilter, portFilter]);

  // ==========================================================
  // RENDER
  // ==========================================================
  return (
    <div className="p-4 max-w-[1600px] mx-auto">
      {/* Header */}
      <button
        onClick={() => navigate("/olt-status")}
        className="mb-4 px-3 py-1 text-sm bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
      >
        ← Volver al dashboard
      </button>

      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">
          ONUs de <span className="text-blue-600">{oltName}</span>
          <span className="text-gray-500 text-base font-normal ml-2">({total} ONUs)</span>
        </h1>
        <button
          onClick={() => { loadData(); loadFilterOptions(); }}
          className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
        >
          🔄 Actualizar
        </button>
      </div>

      {/* Barra de filtros */}
      <div className="bg-white rounded-xl shadow-sm p-4 mb-4">
        {/* Búsqueda global */}
        <div className="flex gap-2 mb-3">
          <input
            type="text"
            placeholder="Buscar por nombre, modelo, PON (0/2/10), ONU ID (0/2/10:5)..."
            className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") applySearch(); }}
          />
          <button
            onClick={applySearch}
            className="px-4 py-2 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
          >
            🔍 Buscar
          </button>
        </div>

        {/* Filtros en fila */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
          <select
            className="border border-gray-300 rounded px-2 py-1.5 text-sm"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
          >
            <option value="all">Estado: todos</option>
            <option value="online">🟢 Online</option>
            <option value="offline">🔴 Offline</option>
            <option value="unknown">⚪ Unknown</option>
          </select>

          <select
            className="border border-gray-300 rounded px-2 py-1.5 text-sm"
            value={powerFilter}
            onChange={(e) => { setPowerFilter(e.target.value); setPage(1); }}
          >
            <option value="all">Potencia: todas</option>
            <option value="bad">⚠️ Fuera de rango</option>
          </select>

          <select
            className="border border-gray-300 rounded px-2 py-1.5 text-sm"
            value={frameFilter}
            onChange={(e) => { setFrameFilter(e.target.value); setPage(1); }}
          >
            <option value="">Frame: todos</option>
            {filterOptions.frames.map((f) => (
              <option key={f} value={f}>Frame {f}</option>
            ))}
          </select>

          <select
            className="border border-gray-300 rounded px-2 py-1.5 text-sm"
            value={slotFilter}
            onChange={(e) => { setSlotFilter(e.target.value); setPage(1); }}
          >
            <option value="">Slot: todos</option>
            {filterOptions.slots.map((s) => (
              <option key={s} value={s}>Slot {s}</option>
            ))}
          </select>

          <select
            className="border border-gray-300 rounded px-2 py-1.5 text-sm"
            value={portFilter}
            onChange={(e) => { setPortFilter(e.target.value); setPage(1); }}
          >
            <option value="">Puerto: todos</option>
            {filterOptions.ports.map((p) => (
              <option key={p} value={p}>Puerto {p}</option>
            ))}
          </select>

          <select
            className="border border-gray-300 rounded px-2 py-1.5 text-sm"
            value={modelFilter}
            onChange={(e) => { setModelFilter(e.target.value); setPage(1); }}
          >
            <option value="">Modelo: todos</option>
            {filterOptions.models.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>

          <select
            className="border border-gray-300 rounded px-2 py-1.5 text-sm"
            value={vlanFilter}
            onChange={(e) => { setVlanFilter(e.target.value); setPage(1); }}
          >
            <option value="">VLAN: todas</option>
            {filterOptions.vlans.map((v) => (
              <option key={v} value={v}>VLAN {v}</option>
            ))}
          </select>

          <button
            onClick={clearAllFilters}
            className="border border-gray-300 rounded px-2 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            disabled={activeFilters.length === 0}
          >
            ✖ Limpiar filtros
          </button>
        </div>

        {/* Chips de filtros activos */}
        {activeFilters.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {activeFilters.map((chip) => (
              <span
                key={chip.key}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-50 text-blue-800 border border-blue-200 rounded-full"
              >
                {chip.label}
                <button
                  onClick={chip.clear}
                  className="text-blue-600 hover:text-blue-900 font-bold"
                  title="Quitar filtro"
                >
                  ✕
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* Tabla */}
      <div className="overflow-x-auto bg-white rounded-xl shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100 border-b">
            <tr>
              <SortableTh field="pon" current={sortField} order={sortOrder} onSort={handleSort}>PON</SortableTh>
              <SortableTh field="name" current={sortField} order={sortOrder} onSort={handleSort}>Cliente</SortableTh>
              <SortableTh field="model" current={sortField} order={sortOrder} onSort={handleSort}>Modelo</SortableTh>
              <SortableTh field="status" current={sortField} order={sortOrder} onSort={handleSort}>Estado</SortableTh>
              <SortableTh field="rx_power" current={sortField} order={sortOrder} onSort={handleSort}>RX (dBm)</SortableTh>
              <SortableTh field="vlan" current={sortField} order={sortOrder} onSort={handleSort}>VLAN</SortableTh>
              <SortableTh field="last_poll" current={sortField} order={sortOrder} onSort={handleSort}>Última vez</SortableTh>
              <SortableTh field="first_seen" current={sortField} order={sortOrder} onSort={handleSort}>Primera vez</SortableTh>
              <th className="px-3 py-2 text-left">Acciones</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr><td colSpan="9" className="px-3 py-8 text-center text-gray-500">Cargando...</td></tr>
            ) : onus.length === 0 ? (
              <tr><td colSpan="9" className="px-3 py-8 text-center text-gray-500">
                No hay ONUs que coincidan con los filtros.
              </td></tr>
            ) : (
              onus.map((onu) => {
                const rx = onu.rx_power;
                const badPower = rx !== null && rx !== undefined && (rx < LOW_SIGNAL_MIN || rx > LOW_SIGNAL_MAX);
                return (
                  <tr key={onu.index} className="border-t hover:bg-gray-50">
                    <td className="px-3 py-2 font-mono text-xs">
                      <span className="font-semibold">{onu.fsp_onu || onu.onu_id}</span>
                    </td>
                    <td className="px-3 py-2 min-w-[280px] max-w-[500px]" title={onu.name || ""}>
                      <div className="truncate">
                        {onu.name || <span className="text-gray-400">Sin nombre</span>}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-600">{onu.model || "—"}</td>
                    <td className="px-3 py-2">
                      {onu.status === "online" ? (
                        <span className="inline-flex items-center gap-1 text-xs bg-green-50 text-green-700 border border-green-200 rounded-full px-2 py-0.5">
                          🟢 Online
                        </span>
                      ) : onu.status === "offline" ? (
                        <span className="inline-flex items-center gap-1 text-xs bg-red-50 text-red-700 border border-red-200 rounded-full px-2 py-0.5">
                          🔴 Offline
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-600 border border-gray-200 rounded-full px-2 py-0.5">
                          ⚪ {onu.status || "unknown"}
                        </span>
                      )}
                    </td>
                    <td className={`px-3 py-2 text-sm ${rxBadgeClass(rx)}`}>
                      {fmtRxPower(rx)}
                      {badPower && <span className="ml-1 text-xs">⚠️</span>}
                    </td>
                    <td className="px-3 py-2 text-xs font-mono">
                      {onu.vlan !== null && onu.vlan !== undefined
                        ? <span className="bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded">{onu.vlan}</span>
                        : <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500">
                      {fmtRelativeTime(onu.last_poll)}
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-500">
                      {fmtRelativeTime(onu.first_seen)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      <button
                        onClick={() => handleViewONU(onu)}
                        className="bg-blue-600 text-white text-xs px-2 py-1 rounded hover:bg-blue-700 mr-1"
                      >
                        👁 Ver
                      </button>
                      <button
                        onClick={() => handleDeleteONU(onu.index, onu.name)}
                        className="bg-red-600 text-white text-xs px-2 py-1 rounded hover:bg-red-700"
                        title="Eliminar ONU"
                      >
                        🗑
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Paginación */}
      <div className="flex flex-wrap justify-between items-center mt-4 gap-2">
        <div className="flex items-center gap-2">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(p - 1, 1))}
            className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
          >
            ← Anterior
          </button>
          <span className="text-sm text-gray-600">
            Página {page} de {pages} · {total} ONUs
          </span>
          <button
            disabled={page >= pages}
            onClick={() => setPage((p) => Math.min(p + 1, pages))}
            className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300 disabled:opacity-50"
          >
            Siguiente →
          </button>
        </div>

        <select
          className="border border-gray-300 rounded px-2 py-1 text-sm"
          value={limit}
          onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
        >
          <option value={25}>25 por página</option>
          <option value={50}>50 por página</option>
          <option value={100}>100 por página</option>
          <option value={200}>200 por página</option>
          <option value={500}>500 por página</option>
        </select>
      </div>
    </div>
  );
}

// ==========================================================
// COMPONENTE: TH ORDENABLE
// ==========================================================
function SortableTh({ field, current, order, onSort, children }) {
  const isActive = current === field;
  return (
    <th className="px-3 py-2 text-left text-xs font-semibold text-gray-700 select-none">
      <button
        onClick={() => onSort(field)}
        className={`inline-flex items-center gap-1 hover:text-blue-600 ${isActive ? "text-blue-700" : ""}`}
      >
        {children}
        {isActive && (order === "asc" ? "▲" : "▼")}
      </button>
    </th>
  );
}
